import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Calendar, User, 
  Filter, Search, Building, DollarSign,
  AlertCircle, CheckCircle, Clock, FileCheck, Save
} from 'lucide-react';
import { taxFormsService, agentsService } from '../../services';
import W9Viewer from '../TaxForms/W9Viewer';
import Pagination, { usePaged } from '../common/Pagination';
import './TaxFormsManagement.css';

const TaxFormsManagement = () => {
  const [activeTab, setActiveTab] = useState('w9w8');
  const [taxForms, setTaxForms] = useState([]);
  const [generated1099s, setGenerated1099s] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1); // Use previous year for tax forms
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [show1099Modal, setShow1099Modal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'w9w8') {
        // Load submitted tax forms (W-9/W-8BEN)
        const [formsData, agentsData] = await Promise.all([
          taxFormsService.getAll(),
          agentsService.getAll()
        ]);
        
        setTaxForms(formsData);
        setAgents(agentsData);
      } else if (activeTab === 'generated1099s') {
        // Load generated 1099s
        const [generated1099Data, agentsData] = await Promise.all([
          taxFormsService.getGenerated1099s(),
          agentsService.getAll()
        ]);
        
        setGenerated1099s(generated1099Data);
        setAgents(agentsData);
      } else {
        // Load agents for 1099 generation
        const agentsData = await agentsService.getAll();
        setAgents(agentsData.filter(agent => agent.country === 'US'));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate1099 = async (agentId) => {
    try {
      const result = await taxFormsService.generate1099(agentId, selectedYear);
      setSelectedAgent({
        ...result.data,
        name: agents.find(a => a.id === agentId).name,
        email: agents.find(a => a.id === agentId).email
      });
      setSelectedAgentId(agentId); // Store the agent ID separately
      setShow1099Modal(true);
    } catch (error) {
      console.error('Failed to generate 1099:', error);
      alert('Failed to generate 1099. Agent may not have submitted W-9.');
    }
  };

  const handleSave1099 = async () => {
    try {
      await taxFormsService.save1099(selectedAgentId, selectedAgent.tax_year, selectedAgent.total_commission, selectedAgent.w9_data);
      alert('1099 form generated and saved successfully!');
      setShow1099Modal(false);
      // Refresh the data to show the new 1099 form
      loadData();
    } catch (error) {
      console.error('Failed to save 1099:', error);
      alert('Failed to save 1099 form. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/tax-forms/export/csv', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `w9-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export W-9 data. Please try again.');
    }
  };

  const getFormStatusBadge = (agent) => {
    const hasW9 = taxForms.some(f => f.agent_id === agent.id && f.form_type === 'w9');
    const hasW8 = taxForms.some(f => f.agent_id === agent.id && f.form_type === 'w8ben');
    
    if (agent.country === 'US' && hasW9) {
      return <span className="status-badge status-complete">W-9 Complete</span>;
    } else if (agent.country !== 'US' && hasW8) {
      return <span className="status-badge status-complete">W-8BEN Complete</span>;
    } else {
      return <span className="status-badge status-pending">Form Required</span>;
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paged = usePaged(filteredAgents, `${activeTab}|${searchTerm}`);

  const currentYearForms = taxForms.filter(form => 
    new Date(form.created_at).getFullYear() === selectedYear
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tax Forms Management</h1>
        <p className="text-gray-600 mt-2">Manage agent tax forms and generate 1099s</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('w9w8')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'w9w8'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-2" />
              W-9 / W-8BEN Forms
            </button>
            <button
              onClick={() => setActiveTab('1099')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === '1099'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign className="w-4 h-4 inline-block mr-2" />
              1099 Forms
            </button>
            <button
              onClick={() => setActiveTab('generated1099s')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'generated1099s'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-2" />
              Generated 1099s
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          <Pagination {...paged} />
          {activeTab === 'w9w8' ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Total Agents</p>
                      <p className="text-2xl font-bold text-blue-600">{agents.length}</p>
                    </div>
                    <User className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-900">W-9 Forms</p>
                      <p className="text-2xl font-bold text-green-600">
                        {taxForms.filter(f => f.form_type === 'w9').length}
                      </p>
                    </div>
                    <FileCheck className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-900">W-8BEN Forms</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {taxForms.filter(f => f.form_type === 'w8ben').length}
                      </p>
                    </div>
                    <FileCheck className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-yellow-900">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {agents.filter(a => !taxForms.some(f => f.agent_id === a.id)).length}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-500" />
                  </div>
                </div>
              </div>

              {/* Search and Export */}
              <div className="mb-6 flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <Download size={16} />
                  Export W-9 to CSV
                </button>
              </div>

              {activeTab === 'w9w8' && (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  {/* Desktop Table View */}
                  <div className="hidden md:block">
                    <table className="min-w-full admin-table">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Form Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paged.pageItems.map((agent) => {
                          const agentForms = taxForms.filter(f => f.agent_id === agent.id);
                          const latestForm = agentForms.sort((a, b) => b.version - a.version)[0];
                          
                          return (
                            <tr key={agent.id}>
                              <td className="px-6 py-4">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                  <p className="text-sm text-gray-500">{agent.email}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {agent.country || 'Not specified'}
                              </td>
                              <td className="px-6 py-4">
                                {getFormStatusBadge(agent)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {latestForm ? new Date(latestForm.created_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-6 py-4">
                                {agentForms.length > 0 ? (
                                  <button
                                    onClick={() => {
                                      const form = agentForms[0]; // Get the most recent form
                                      setSelectedAgent({
                                        ...form,
                                        name: agent.name,
                                        email: agent.email
                                      });
                                      setShowFormModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    View Form
                                  </button>
                                ) : (
                                  <span className="text-sm text-gray-500">No forms submitted</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Agents List */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <table className="min-w-full tax-forms-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Form Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paged.pageItems.map((agent) => {
                        const agentForms = taxForms.filter(f => f.agent_id === agent.id);
                        const latestForm = agentForms.sort((a, b) => b.version - a.version)[0];
                        
                        return (
                          <tr key={agent.id}>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                <p className="text-sm text-gray-500">{agent.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {agent.country || 'Not specified'}
                            </td>
                            <td className="px-6 py-4">
                              {getFormStatusBadge(agent)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {latestForm ? new Date(latestForm.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4">
                              {agentForms.length > 0 ? (
                                <button
                                  onClick={() => {
                                    const form = agentForms[0]; // Get the most recent form
                                    setSelectedAgent({
                                      ...form,
                                      name: agent.name,
                                      email: agent.email
                                    });
                                    setShowFormModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  View Form
                                </button>
                              ) : (
                                <span className="text-sm text-gray-500">No forms submitted</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : activeTab === '1099' ? (
            // 1099 Tab
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Tax Year:</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[2024, 2023, 2022, 2021].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 1099 Eligible Agents */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">1099 Forms Information</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      1099 forms are generated for US-based agents who have submitted W-9 forms and have received commission payments during the tax year.
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <table className="min-w-full tax-forms-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">W-9 Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paged.pageItems.map((agent) => {
                        const hasW9 = taxForms.some(f => f.agent_id === agent.id && f.form_type === 'w9');
                        
                        return (
                          <tr key={agent.id}>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                <p className="text-sm text-gray-500">{agent.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {hasW9 ? (
                                <span className="status-badge status-complete">W-9 Complete</span>
                              ) : (
                                <span className="status-badge status-pending">W-9 Required</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {hasW9 ? (
                                <button
                                  onClick={() => handleGenerate1099(agent.id)}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Generate 1099
                                </button>
                              ) : (
                                <span className="text-sm text-gray-500">W-9 Required</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            // Generated 1099s Tab
            <>
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <table className="min-w-full tax-forms-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">1099 Form Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paged.pageItems.map((agent) => {
                        const has1099 = generated1099s.some(f => f.agent_id === agent.id);
                        
                        return (
                          <tr key={agent.id}>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                <p className="text-sm text-gray-500">{agent.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {has1099 ? (
                                <span className="status-badge status-complete">1099 Complete</span>
                              ) : (
                                <span className="status-badge status-pending">1099 Required</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {has1099 ? (
                                <button
                                  onClick={() => {
                                    setSelectedAgent({ ...agent, form: generated1099s.find(f => f.agent_id === agent.id) });
                                    setShowFormModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  View 1099
                                </button>
                              ) : (
                                <span className="text-sm text-gray-500">1099 Required</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Form Details Modal */}
      {showFormModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {activeTab === 'generated1099s' ? '1099-MISC Form Details' : 'Tax Form Details'}
              </h2>
            </div>
            <div className="p-6">
              {activeTab === 'generated1099s' && selectedAgent.form ? (
                // Generated 1099 Display
                <>
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="font-medium text-gray-900 mb-4">Payer Information</h3>
                    <p className="text-sm">MainStreet Travel Agency, Inc.</p>
                    <p className="text-sm">929 W. Sunset Blvd. STE 21-292</p>
                    <p className="text-sm">St. George, UT 84770</p>
                    <p className="text-sm">TIN: 87-0968282</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="font-medium text-gray-900 mb-4">Recipient Information</h3>
                    <p className="text-sm"><span className="font-medium">Name:</span> {selectedAgent.name}</p>
                    <p className="text-sm"><span className="font-medium">Email:</span> {selectedAgent.email}</p>
                    <p className="text-sm"><span className="font-medium">Tax Year:</span> {selectedAgent.form.tax_year}</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="font-medium text-gray-900 mb-4">Income Information</h3>
                    <p className="text-lg">
                      <span className="font-medium">Box 3 - Other Income:</span> 
                      <span className="ml-2 text-2xl font-bold">${selectedAgent.form.total_amount?.toLocaleString() || '0.00'}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      Generated on: {new Date(selectedAgent.form.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </>
              ) : (
                // Submitted Tax Form Display (W-9/W-8BEN)
                <>
                  {selectedAgent && (
                    <>
                      <div className="space-y-6">
                        {selectedAgent.form_type === 'w9' ? (
                          <W9Viewer formData={selectedAgent.form_data} />
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                            {selectedAgent.form_data && typeof selectedAgent.form_data === 'object' ? (
                              <div className="space-y-2">
                                {Object.entries(selectedAgent.form_data).map(([key, value]) => (
                                  <div key={key} className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    </span>
                                    <span className="text-sm text-gray-900">
                                      {value?.toString() || 'N/A'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No form data available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowFormModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1099 Preview Modal */}
      {show1099Modal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">1099-MISC Preview - Tax Year {selectedYear}</h2>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-4">Payer Information</h3>
                <p className="text-sm">MainStreet Travel Agency, Inc.</p>
                <p className="text-sm">929 W. Sunset Blvd. STE 21-292</p>
                <p className="text-sm">St. George, UT 84770</p>
                <p className="text-sm">TIN: 87-0968282</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-4">Recipient Information</h3>
                <p className="text-sm"><span className="font-medium">Name:</span> {selectedAgent.w9_data?.form_data?.name || 'Not available'}</p>
                <p className="text-sm"><span className="font-medium">TIN:</span> {selectedAgent.w9_data?.form_data?.tin || 'Not available'}</p>
                <p className="text-sm"><span className="font-medium">Address:</span> {
                  selectedAgent.w9_data?.form_data ? 
                    `${selectedAgent.w9_data.form_data.address || ''}, ${selectedAgent.w9_data.form_data.city || ''}, ${selectedAgent.w9_data.form_data.state || ''} ${selectedAgent.w9_data.form_data.zipCode || ''}`.replace(/^,\s*|,\s*$/, '').replace(/,\s*,/g, ',') 
                    : 'Not available'
                }</p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-medium text-gray-900 mb-4">Income Information</h3>
                <p className="text-lg">
                  <span className="font-medium">Box 3 - Other Income:</span> 
                  <span className="ml-2 text-2xl font-bold">${selectedAgent.total_commission?.toLocaleString() || '0.00'}</span>
                </p>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShow1099Modal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleSave1099}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Generate & Save
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download 1099
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxFormsManagement;
